import React, { useCallback } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  Chip,
  Avatar,
  Divider
} from "@mui/material";
import { 
  Visibility as ViewIcon, 
  Edit as EditIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Web as WebIcon
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import {
  LOCAL_STORAGE_CURRENT_ACCOUNT,
  setLocalStorage,
} from "../../utils/localStorage";

const AccountCard = ({ account, i, siteCount = 0 }) => {
  const { id, name, accountType, subscriptionStatus } = account;
  const history = useNavigate();

  const handleShowSites = useCallback(() => {
    setLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT, account);
    history(`/account/${account.id}/sites`);
  }, [account, history]);

  const handleShowOverview = useCallback(() => {
    setLocalStorage(LOCAL_STORAGE_CURRENT_ACCOUNT, account);
    history(`/account-overview/${account.id}`);
  }, [account, history]);

  function getSpecificData(text) {
    return text.length > 22 ? `${String(text.slice(0, 22))}...` : text;
  }

  // Helper function to determine subscription status styling
  const getStatusStyles = () => {
    if (subscriptionStatus === 'active') {
      return {
        color: 'success.main',
        bgcolor: 'rgba(76, 175, 80, 0.08)',
        icon: null,
        label: 'Active'
      };
    } else if (subscriptionStatus === 'pending') {
      return {
        color: 'warning.main',
        bgcolor: 'rgba(255, 152, 0, 0.08)',
        icon: <WarningIcon fontSize="small" />,
        label: 'Pending'
      };
    } else {
      return {
        color: 'error.main',
        bgcolor: 'rgba(244, 67, 54, 0.08)',
        icon: <WarningIcon fontSize="small" />,
        label: 'Inactive'
      };
    }
 
  };

  const statusStyles = getStatusStyles();

  return (
    <Grid item xs={12} sm={6} md={4} key={i}>
      <Paper
        elevation={0}
        sx={{
          border: `2px double`, 
          borderColor: statusStyles.color,
          borderRadius: 2,
          overflow: 'hidden',
          height: '100%',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 8px 16px 0 rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-4px)',
          }
        }}
      >
        
        {/* Card Content */}
        <Box sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar 
              sx={{ 
                bgcolor: 'primary.light',
                width: 48,
                height: 48
              }}
            >
              <BusinessIcon />
            </Avatar>
            
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {getSpecificData(name)}
                </Typography>
                
                {subscriptionStatus && (
                  <Chip
                    size="small"
                    icon={statusStyles.icon}
                    label={statusStyles.label}
                    sx={{ 
                      color: statusStyles.color,
                      bgcolor: statusStyles.bgcolor,
                      fontWeight: 500,
                      borderRadius: '4px'
                    }}
                  />
                )}
              </Stack>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {accountType}
              </Typography>
              
              {/* Site Count Display */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <WebIcon 
                  fontSize="small" 
                  sx={{ color: 'primary.main', fontSize: 16 }} 
                />
                <Typography 
                  variant="body2" 
                  color="primary.main"
                  sx={{ fontWeight: 500 }}
                >
                  {siteCount} {siteCount === 1 ? 'site' : 'sites'}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>
        
        <Divider />
        
        {/* Actions */}
        <Box sx={{ p: 2 }}>
          {subscriptionStatus === 'active' ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                startIcon={<ViewIcon />}
                onClick={handleShowSites}
                sx={{ 
                  borderRadius: '8px',
                  flex: 1
                }}
              >
                View Sites
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleShowOverview}
                sx={{ 
                  borderRadius: '8px',
                  flex: 1
                }}
              >
                Manage
              </Button>
            </Stack>
          ) : (
            <Button
              fullWidth
              size="small"
              color="warning"
              variant="contained"
              onClick={() => history(`/account/${id}/sites`)}
              sx={{ borderRadius: '8px' }}
            >
              Activate Collection
            </Button>
          )}
        </Box>
      </Paper>
    </Grid>
  );
};

export default AccountCard;
